import { useTheme } from "@mui/material";
import { useMemo } from "react";

import { DashboardColor } from "@/components/screens/ServiceDashboard/components/styles";

type Props = {
  values: (number | null)[];
  color?: DashboardColor | string;
};

type Pt = { x: number; y: number };

const WIDTH = 120;
const HEIGHT = 32;

/**
 * Minigráfico alinhado ao eixo de dias: cada ponto fica no seu dia.
 * Dias sem valor viram lacunas ligadas por tracejado leve; dia isolado vira ponto.
 */
const Sparkline = ({ values, color = "info" }: Props) => {
  const theme = useTheme();
  const stroke =
    (theme.palette as unknown as Record<string, { main?: string }>)[color]?.main ?? color;

  const { segments, gaps, dots } = useMemo(() => {
    const numeric = values.filter((value): value is number => value !== null);
    const empty = { segments: [] as Pt[][], gaps: [] as [Pt, Pt][], dots: [] as Pt[] };
    if (!numeric.length) return empty;

    const max = Math.max(...numeric);
    const min = Math.min(...numeric);
    const range = max - min || 1;
    const step = values.length > 1 ? WIDTH / (values.length - 1) : 0;
    const pad = 3;

    // Cada valor fica sempre na posição do seu dia (índice da série).
    const segs: Pt[][] = [];
    let current: Pt[] = [];
    values.forEach((value, index) => {
      if (value === null) {
        if (current.length) segs.push(current);
        current = [];
        return;
      }
      const y =
        max === min ? HEIGHT / 2 : HEIGHT - pad - ((value - min) / range) * (HEIGHT - pad * 2);
      current.push({ x: values.length > 1 ? step * index : WIDTH / 2, y });
    });
    if (current.length) segs.push(current);

    const gapLinks: [Pt, Pt][] = [];
    for (let i = 1; i < segs.length; i += 1) {
      gapLinks.push([segs[i - 1][segs[i - 1].length - 1], segs[i][0]]);
    }
    return {
      segments: segs.filter((seg) => seg.length > 1),
      gaps: gapLinks,
      dots: segs.filter((seg) => seg.length === 1).map((seg) => seg[0]),
    };
  }, [values]);

  if (!segments.length && !dots.length) return null;

  const toPath = (pts: Pt[]) =>
    pts.map((p, j) => `${j ? "L" : "M"} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(" ");

  return (
    <svg
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      width="100%"
      height="2.25rem"
      preserveAspectRatio="none"
      aria-hidden
      style={{ overflow: "visible", display: "block" }}
    >
      {segments.map((seg, i) => {
        const line = toPath(seg);
        const first = seg[0];
        const last = seg[seg.length - 1];
        return (
          <g key={`s${i}`}>
            <path
              d={`${line} L ${last.x.toFixed(1)} ${HEIGHT} L ${first.x.toFixed(1)} ${HEIGHT} Z`}
              fill={stroke}
              fillOpacity={0.12}
              stroke="none"
            />
            <path
              d={line}
              fill="none"
              stroke={stroke}
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
            />
          </g>
        );
      })}
      {gaps.map(([a, b], i) => (
        <path
          key={`g${i}`}
          d={toPath([a, b])}
          fill="none"
          stroke={stroke}
          strokeOpacity={0.45}
          strokeWidth="1.2"
          strokeDasharray="3 3"
          vectorEffect="non-scaling-stroke"
        />
      ))}
      {dots.map((p, i) => (
        <line
          key={`d${i}`}
          x1={p.x}
          y1={p.y}
          x2={p.x}
          y2={p.y}
          stroke={stroke}
          strokeWidth="4"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
      ))}
    </svg>
  );
};

export default Sparkline;
